import styled from 'styled-components';
import { ProgressMessage } from './ProgressMessage';
import { getFormCompletionStats, getNextRequiredEmptyField } from '../../lib/formFiller';
import { useMemo, useState } from 'react';
import {
  useFormFillerRole,
  useFormFillerToken,
  useLoadedFormFillerInitialData,
  useFormFillerBasePdfDownloadUrl,
  useCheckboxFeature,
  useFormFillerShowLogo,
} from './hooks';
import { FormField, RawFormFillerData } from '../../types/formFiller';
import { SaveStatus } from './SaveStatus';
import { FormTopBar } from 'shared-utils/components/forms/FormTopBar';
import { FormInfo } from './FormInfo';
import Button from 'design-system/src/components/Button/Button';
import { Tooltip } from 'design-system/src/components/Tooltip/Tooltip';
import { ProviderAttestationBanner } from './ProviderAttestationBanner';
import { CheckboxSubmitData } from './FormFillerProvider';

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export function FormFillerInProgressHeader({
  formData,
  onFocusField,
  onSubmit,
  saveStatus,
}: {
  formData: RawFormFillerData;
  onFocusField: (field: FormField) => void;
  onSubmit: (checkboxData?: CheckboxSubmitData) => Promise<void>;
  saveStatus: 'saving' | 'saved' | 'failed';
}) {
  const [hasStarted, setHasStarted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentFieldId, setCurrentFieldId] = useState<string | null>(null);
  const [providerAttestedPatientConsent, setProviderAttestedPatientConsent] = useState(false);

  const providerAttestPatientConsentConfig = useCheckboxFeature('providerAttestedPatientConsent');
  const handleStartClick = () => {
    setHasStarted(true);
    const firstEmptyField = getNextRequiredEmptyField({
      initialData: initialData,
      rawFormData: formData,
      role: role,
      currentFieldId: null,
    });
    if (firstEmptyField) {
      setCurrentFieldId(firstEmptyField.id);
      onFocusField(firstEmptyField);
    }
  };

  const handleNextClick = () => {
    const nextField = getNextRequiredEmptyField({
      initialData: initialData,
      rawFormData: formData,
      role: role,
      currentFieldId: currentFieldId,
    });
    if (nextField) {
      setCurrentFieldId(nextField.id);
      onFocusField(nextField);
    }
  };

  const initialData = useLoadedFormFillerInitialData();
  const role = useFormFillerRole();
  const token = useFormFillerToken();
  const showLogo = useFormFillerShowLogo();
  const getBasePdfDownloadUrl = useFormFillerBasePdfDownloadUrl();
  const completionStats = useMemo(() => {
    return getFormCompletionStats({
      initialData: initialData,
      rawFormData: formData,
      role: role,
    });
  }, [initialData, formData, role]);

  const showProviderAttestPatientConsentBanner =
    providerAttestPatientConsentConfig !== undefined &&
    providerAttestPatientConsentConfig.drugName !== null &&
    role === 'Prescriber';

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      // Build checkbox data object
      const checkboxData: CheckboxSubmitData = {};
      if (showProviderAttestPatientConsentBanner) {
        checkboxData.providerAttestedPatientConsent = providerAttestedPatientConsent;
      }
      // Only pass checkboxData if there's any data
      await onSubmit(Object.keys(checkboxData).length > 0 ? checkboxData : undefined);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormTopBar
      showLogo={showLogo}
      leftContent={
        <FormInfo formName={initialData.displayTitle}>
          <ProgressMessage completionStats={completionStats} />
        </FormInfo>
      }
      rightContent={
        <ActionButtons>
          <Tooltip content="Downloads a blank PDF to print and fill out by hand">
            <span>
              <Button variant="secondary" href={getBasePdfDownloadUrl(token)} target="_blank">
                Download
              </Button>
            </span>
          </Tooltip>
          <SaveStatus state={saveStatus} />
          {completionStats.isComplete ? (
            <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} isLoading={isSubmitting}>
              Submit
            </Button>
          ) : !hasStarted ? (
            <Button variant="primary" onClick={handleStartClick}>
              Start
            </Button>
          ) : (
            <Button variant="primary" onClick={handleNextClick} disabled={isSubmitting} isLoading={isSubmitting}>
              Next
            </Button>
          )}
        </ActionButtons>
      }
      bottomContent={
        showProviderAttestPatientConsentBanner ? (
          <ProviderAttestationBanner
            drugName={providerAttestPatientConsentConfig.drugName}
            checked={providerAttestedPatientConsent}
            onChange={setProviderAttestedPatientConsent}
          />
        ) : null
      }
    />
  );
}
